import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { OrganisationContext } from '../../../../context';
import { ChannelRole, channelsNewApi, organisationApi } from '../../../../services';
import {
  Button,
  ButtonType,
  InputField,
  ListOpenDirection,
  Select,
  Switch,
  useAppSelector,
} from '../../../../shared';

import classes from './Staff.module.scss';

export const Staff: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardStaff' });

  const [organisationUpdate] = organisationApi.endpoints.organisationUpdate.useMutation();

  const [getChannels] = channelsNewApi.endpoints.channels.useLazyQuery();

  const { channels } = useAppSelector(({ channels }) => channels);

  const { organisation, setOrganisation } = useContext(OrganisationContext);

  const {
    uuid: orgUuid,
    invitingIntoDefaultChannelsEnabled: orgInvitingIntoDefaultChannelsEnabled,
    defaultChannelUserRoleId: orgDefaultChannelUserRoleId,
  } = organisation;

  const [invitingIntoDefaultChannelsEnabled, setInvitingIntoDefaultChannelsEnabled] =
    useState<boolean>(orgInvitingIntoDefaultChannelsEnabled);

  const [defaultChannelUserRoleId, setDefaultChannelUserRoleId] = useState<number>(
    orgDefaultChannelUserRoleId
  );

  const [defaultChannelIds, setDefaultChannelIds] = useState<number[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const channelItems = useMemo(
    () =>
      channels.map(({ id, name }) => {
        return { id, title: name };
      }),
    [channels]
  );

  const getDefaultChannelIds = useCallback(async () => {
    if (defaultChannelIds.length) {
      return;
    }

    setDefaultChannelIds(
      (await getChannels({ filters: { onlyDefault: true } }).unwrap()).map(({ id }) => id)
    );
  }, [defaultChannelIds.length, getChannels]);

  useEffect(() => {
    if (!orgInvitingIntoDefaultChannelsEnabled) {
      return;
    }

    getDefaultChannelIds();
  }, [getDefaultChannelIds, orgInvitingIntoDefaultChannelsEnabled]);

  const onSave = useCallback(async () => {
    setLoading(true);

    try {
      const payload = await toast.promise(
        organisationUpdate({
          fields: {
            invitingIntoDefaultChannelsEnabled,
            defaultChannelIds,
            defaultChannelUserRoleId,
          },
        }).unwrap(),
        { pending: t('orgDashboard.save-pending'), success: t('orgDashboard.save-success') }
      );

      if (payload) {
        setOrganisation?.({ ...organisation, ...payload });
        return;
      }
    } catch (_) {
      toast.error(t('orgDashboard.save-error'));
    } finally {
      setLoading(false);
    }
  }, [
    defaultChannelIds,
    defaultChannelUserRoleId,
    invitingIntoDefaultChannelsEnabled,
    organisation,
    organisationUpdate,
    setOrganisation,
    t,
  ]);

  return (
    <div className={classes['staff']}>
      <div className={classes['staff__section']}>
        <h2 className={classes['staff__section-title']}>{ctxT('tech-id')}</h2>
        <InputField value={orgUuid} label={'UUID'} disabled />
      </div>

      <div className={classes['staff__section']}>
        <Switch
          className={classes['staff__section-title']}
          checked={invitingIntoDefaultChannelsEnabled}
          onChange={(allowed) => setInvitingIntoDefaultChannelsEnabled(allowed)}
          label={ctxT('default-channels')}
        />

        {invitingIntoDefaultChannelsEnabled && (
          <>
            <Select
              label={ctxT('user-role')}
              selectedItemId={defaultChannelUserRoleId}
              items={[
                { id: ChannelRole.READER, title: t('roles.reader') },
                { id: ChannelRole.EDITOR_LIMITED, title: t('roles.editor-limited') },
                { id: ChannelRole.EDITOR, title: t('roles.editor') },
                { id: ChannelRole.ADMIN, title: t('roles.admin') },
              ]}
              onChange={(roleId) => setDefaultChannelUserRoleId(roleId as number)}
            />

            <Select
              label={ctxT('channels')}
              selectedItemIds={defaultChannelIds}
              items={channelItems}
              onMultiCheckChange={setDefaultChannelIds}
              listOpenDirection={ListOpenDirection.top}
              listMaxHeightRem={16}
              disabled={!Boolean(channelItems.length)}
            />
          </>
        )}
      </div>

      <Button
        label={t('common.save')}
        type={ButtonType.primary}
        onClick={onSave}
        disabled={loading}
      />
    </div>
  );
};
